import { forwardRef } from 'react';
import { cn } from '../utils/cn';
import { Icon } from '../primitives/Icon';
import { Button } from '../primitives/Button';

export interface ErrorStateProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  detail: string;
  /** Shown verbatim so a user can quote it to support. */
  correlationId?: string | null;
  onRetry?: () => void;
  retryLabel?: string;
  actions?: React.ReactNode;
  size?: 'sm' | 'md';
}

export const ErrorState = forwardRef<HTMLDivElement, ErrorStateProps>(function ErrorState(
  {
    title,
    detail,
    correlationId,
    onRetry,
    retryLabel = 'Try again',
    actions,
    size = 'md',
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <div
      ref={ref}
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-2 text-center',
        size === 'md' ? 'px-6 py-12' : 'px-4 py-6',
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className="flex h-10 w-10 items-center justify-center rounded-full border border-danger-subtle bg-danger-subtle text-danger"
      >
        <Icon name="alert" size={18} />
      </span>
      <h3 className="text-body-lg font-semibold text-content">{title}</h3>
      <p className="max-w-md text-dense-lg text-content-secondary">{detail}</p>
      {children}
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        {onRetry ? (
          <Button variant="primary" size="sm" iconStart="refresh" onClick={onRetry}>
            {retryLabel}
          </Button>
        ) : null}
        {actions}
      </div>
      {correlationId ? (
        <p className="mt-1 font-mono text-micro text-content-muted">Reference {correlationId}</p>
      ) : null}
    </div>
  );
});
